import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Mail, MessageCircle, Save, Send, Settings } from "lucide-react";
import { toast } from "sonner";

export default function AdminNotifications() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/notifications");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...cfg };
      // Don't overwrite secrets if they look masked (****xxxx)
      if (cfg.email_api_key?.startsWith("****")) delete payload.email_api_key;
      if (cfg.twilio_auth_token?.startsWith("****")) delete payload.twilio_auth_token;
      await api.patch("/admin/notifications", payload);
      toast.success("Configurações salvas");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const test = async (channel) => {
    if (!testTo) { toast.error("Informe destino de teste"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notifications/test-send", { channel, to: testTo });
      if (data.ok) toast.success(`${channel} enviado!`);
      else toast.warning(`${channel}: ${data.skipped || data.error || "(modo log)"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="p-10 text-center text-stone-500">Carregando...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/admin" className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mb-3"><ArrowLeft size={14}/> Voltar</Link>
      <h1 className="font-display text-3xl font-bold text-stone-900 flex items-center gap-2"><Settings size={26}/> Notificações & Verificação</h1>
      <p className="text-stone-500 mt-1 text-sm">Configure canais de e-mail (Resend) e WhatsApp (Twilio). Quando configurado, será usado para envio de códigos de verificação no cadastro.</p>

      <Card className="mt-6 p-5 rounded-2xl border-blue-200 bg-blue-50">
        <label className="flex items-center gap-2 text-sm font-semibold text-blue-950">
          <Switch checked={!!cfg.log_mode} onCheckedChange={(v) => update({ log_mode: v })} data-testid="cfg-log-mode"/>
          Modo de log (escreve códigos no log do backend — útil pra dev/teste)
        </label>
      </Card>

      <Card className="mt-4 p-6 rounded-2xl border-stone-200">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-xl font-semibold text-stone-900 flex items-center gap-2"><Mail size={20}/> E-mail (Resend)</h2>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!cfg.email_enabled} onCheckedChange={(v) => update({ email_enabled: v })} data-testid="cfg-email-enabled"/> Habilitar
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>API Key Resend</Label><Input type="password" value={cfg.email_api_key || ""} onChange={(e) => update({ email_api_key: e.target.value })} placeholder="re_..." className="rounded-xl mt-1 font-mono text-xs" data-testid="cfg-email-key"/></div>
          <div><Label>Nome do remetente</Label><Input value={cfg.email_from_name || ""} onChange={(e) => update({ email_from_name: e.target.value })} className="rounded-xl mt-1"/></div>
          <div><Label>E-mail remetente</Label><Input value={cfg.email_from || ""} onChange={(e) => update({ email_from: e.target.value })} placeholder="noreply@seudominio.com" className="rounded-xl mt-1" data-testid="cfg-email-from"/></div>
        </div>
        <p className="text-[11px] text-stone-500 mt-2">Em <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-blue-700 underline">resend.com</a>, crie a chave em &gt; API Keys, e verifique seu domínio em &gt; Domains. O e-mail remetente precisa estar verificado.</p>
      </Card>

      <Card className="mt-4 p-6 rounded-2xl border-stone-200">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-display text-xl font-semibold text-stone-900 flex items-center gap-2"><MessageCircle size={20}/> WhatsApp (Twilio)</h2>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!cfg.whatsapp_enabled} onCheckedChange={(v) => update({ whatsapp_enabled: v })} data-testid="cfg-wa-enabled"/> Habilitar
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Account SID</Label><Input value={cfg.twilio_account_sid || ""} onChange={(e) => update({ twilio_account_sid: e.target.value })} placeholder="AC..." className="rounded-xl mt-1 font-mono text-xs" data-testid="cfg-twilio-sid"/></div>
          <div><Label>Auth Token</Label><Input type="password" value={cfg.twilio_auth_token || ""} onChange={(e) => update({ twilio_auth_token: e.target.value })} placeholder="*****" className="rounded-xl mt-1 font-mono text-xs" data-testid="cfg-twilio-token"/></div>
          <div className="sm:col-span-2"><Label>Número WhatsApp remetente</Label><Input value={cfg.twilio_whatsapp_from || ""} onChange={(e) => update({ twilio_whatsapp_from: e.target.value })} placeholder="whatsapp:+14155238886" className="rounded-xl mt-1 font-mono text-xs" data-testid="cfg-twilio-from"/></div>
        </div>
        <p className="text-[11px] text-stone-500 mt-2">Crie conta em <a href="https://twilio.com" target="_blank" rel="noreferrer" className="text-blue-700 underline">twilio.com</a> &gt; Console &gt; Account Info para SID/Token. Sandbox WA padrão: <code>whatsapp:+14155238886</code>.</p>
      </Card>

      <div className="mt-5 flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="cfg-save"><Save size={14} className="mr-1.5"/>{busy ? "Salvando..." : "Salvar configurações"}</Button>
      </div>

      <Card className="mt-6 p-5 rounded-2xl border-amber-200 bg-amber-50">
        <h3 className="font-display font-semibold text-amber-900 mb-3 flex items-center gap-2"><Send size={16}/> Testar envio</h3>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label>Destinatário (email ou +55...)</Label>
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} className="rounded-xl mt-1" placeholder="ex.: voce@email.com ou +5579999999999" data-testid="cfg-test-to"/>
          </div>
          <Button onClick={() => test("email")} variant="outline" className="rounded-xl" data-testid="cfg-test-email"><Mail size={13} className="mr-1"/> Enviar e-mail</Button>
          <Button onClick={() => test("whatsapp")} variant="outline" className="rounded-xl" data-testid="cfg-test-wa"><MessageCircle size={13} className="mr-1"/> Enviar WhatsApp</Button>
        </div>
      </Card>
    </div>
  );
}
